import { useState } from "react";
import {
  ChevronLeft, ChevronRight, Mail, MoreHorizontal, Pencil, Plus, Search,
  ShieldCheck, UserMinus, UserX, UsersRound,
} from "lucide-react";

import { UserFormDialog } from "@/components/UserFormDialog";
import { useConfirm } from "@/components/confirm-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useUsers } from "@/hooks/useUsers";
import { useDeactivateUser, useResendInvite } from "@/hooks/useUserMutations";
import { formatDate, initials } from "@/lib/format";
import type { CurrentUser, Role, UserRow } from "@/lib/types";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Helpers — "can I touch this row?" mirrors private.utils.can_manage_user.
// Server enforces too; this just decides whether to render the menu.
// ---------------------------------------------------------------------------
const ROLE_RANK: Record<Role, number> = {
  user: 0, director: 1, ceo: 2, admin: 3,
};
function canManageRow(actor: CurrentUser | null | undefined, row: UserRow): boolean {
  if (!actor) return false;
  if (actor.id === row.id) return true;          // always edit yourself
  if (actor.is_admin_level) return true;          // admin manages everyone
  // Strict-below-rank ceiling for everyone else.
  return ROLE_RANK[actor.role] > ROLE_RANK[row.role];
}

const ROLES: { value: Role | ""; label: string }[] = [
  { value: "",         label: "Any role" },
  { value: "user",     label: "User" },
  { value: "director", label: "Director" },
  { value: "ceo",      label: "CEO" },
  { value: "admin",    label: "Admin" },
];

const ACTIVE: { value: "" | "true" | "false"; label: string }[] = [
  { value: "",      label: "Any status" },
  { value: "true",  label: "Active" },
  { value: "false", label: "Inactive" },
];

const PAGE_SIZE = 20;

/**
 * /users — staff directory. Avatar + name + role badge + department.
 * Director+ only (RequireAuth + API permission both enforce).
 */
export default function Users() {
  const { data: actor } = useCurrentUser();
  const [search,      setSearch]      = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [role,        setRole]        = useState<Role | "">("");
  const [active,      setActive]      = useState<"" | "true" | "false">("");
  const [page,        setPage]        = useState(1);
  const [inviteOpen,  setInviteOpen]  = useState(false);
  const [editing,     setEditing]     = useState<UserRow | null>(null);

  const { data, isLoading, isFetching } = useUsers({
    search: search || undefined, role: role || undefined, active: active || undefined, page,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Staff directory</h1>
          <p className="text-sm text-muted-foreground">
            People you can manage, scoped by your role in the hierarchy.
          </p>
        </div>
        <Button onClick={() => setInviteOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />Invite user
        </Button>
      </header>

      <UserFormDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <UserFormDialog
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        user={editing}
      />

      <Card className="border-border/60 shadow-soft">
        <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center">
          <form
            className="relative flex-1"
            onSubmit={(e) => { e.preventDefault(); setSearch(searchDraft.trim()); setPage(1); }}
          >
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search name, email, position…"
              className="h-10 pl-9"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
            />
          </form>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={role}
              onChange={(e) => { setRole(e.target.value as Role | ""); setPage(1); }}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm font-medium"
            >
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <select
              value={active}
              onChange={(e) => { setActive(e.target.value as "" | "true" | "false"); setPage(1); }}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm font-medium"
            >
              {ACTIVE.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden border-border/60 shadow-soft">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-muted/30">
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5">Member</TableHead>
                <TableHead className="hidden sm:table-cell">Role</TableHead>
                <TableHead className="hidden lg:table-cell">Department</TableHead>
                <TableHead className="hidden md:table-cell">Status</TableHead>
                <TableHead className="hidden xl:table-cell">Last seen</TableHead>
                <TableHead className="w-16 pr-5 text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6} className="py-4">
                      <Skeleton className="h-10 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : data && data.results.length > 0 ? (
                data.results.map((u) => (
                  <TableRow key={u.id} className="border-border/60">
                    <TableCell className="py-3 pl-5">
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar className="h-9 w-9 ring-1 ring-border">
                          <AvatarFallback className="bg-gradient-to-br from-primary to-emerald-700 text-[11px] font-semibold text-primary-foreground">
                            {initials(u.full_name || u.email)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium">
                            {u.full_name || u.email}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            {u.email}{u.position && <> · {u.position}</>}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <RoleBadge role={u.role} />
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                      {u.department_name || "—"}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {u.is_active
                        ? <Badge variant="outline" className="border bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30">Active</Badge>
                        : <Badge variant="outline" className="border bg-muted text-muted-foreground border-border"><UserX className="mr-1 h-3 w-3" />Inactive</Badge>
                      }
                    </TableCell>
                    <TableCell className="hidden xl:table-cell text-sm text-muted-foreground">
                      {formatDate(u.last_login)}
                    </TableCell>
                    <TableCell className="pr-5 text-right">
                      {canManageRow(actor, u) && (
                        <UserRowActions row={u} onEdit={() => setEditing(u)} />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="py-16">
                    <div className="text-center">
                      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                        <UsersRound className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <h3 className="text-sm font-semibold">No users match</h3>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Try clearing a filter, or invite someone new.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between gap-4">
        <div className="text-sm text-muted-foreground">
          {data ? (
            <>
              <span className="font-mono font-semibold tabular-nums">{data.count.toLocaleString()}</span> member{data.count === 1 ? "" : "s"}
              {isFetching && <span className="ml-2 text-xs">loading…</span>}
            </>
          ) : "—"}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={!data?.previous}
            onClick={() => setPage((p) => Math.max(1, p - 1))}>
            <ChevronLeft className="h-4 w-4" />Previous
          </Button>
          <div className="rounded-md border bg-card px-3 py-1.5 text-sm font-mono tabular-nums text-muted-foreground">
            {page} / {totalPages}
          </div>
          <Button variant="outline" size="sm" disabled={!data?.next}
            onClick={() => setPage((p) => p + 1)}>
            Next<ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function RoleBadge({ role }: { role: Role }) {
  // Match the topbar role-chip colour scheme.
  const className = role === "admin"
    ? "bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-500/30"
    : role === "ceo"
      ? "bg-primary/10 text-primary border-primary/20"
      : role === "director"
        ? "bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/30"
        : "bg-muted/40 text-foreground/80 border-border";
  return (
    <Badge variant="outline" className={cn("border font-medium uppercase", className)}>
      <ShieldCheck className="mr-1 h-3 w-3" />
      {role}
    </Badge>
  );
}

/** Per-row kebab menu — Edit / Deactivate. Visibility gated upstream
 *  via `canManageRow`; the API re-checks too. */
function UserRowActions({
  row, onEdit,
}: {
  row: UserRow;
  onEdit: () => void;
}) {
  const deactivate = useDeactivateUser();
  const resend     = useResendInvite();
  const confirm    = useConfirm();

  async function onDeactivate() {
    const ok = await confirm({
      title: `Deactivate ${row.full_name || row.email}?`,
      description: "They won't be able to sign in until reactivated.",
      confirmLabel: "Deactivate",
      destructive: true,
    });
    if (!ok) return;
    deactivate.mutate(row.id);
  }

  async function onResend() {
    const ok = await confirm({
      title: `Resend invite to ${row.full_name || row.email}?`,
      description:
        "They'll get a fresh set-password link. The previous link (if any) will stop working.",
      confirmLabel: "Send invite",
    });
    if (!ok) return;
    resend.mutate(row.id, {
      onSuccess: (data) => {
        // Re-use the confirm dialog as an acknowledge prompt.
        confirm({
          title: "Invite sent",
          description: data.detail,
          confirmLabel: "OK",
          hideCancel: true,
        });
      },
      onError: (err) => {
        const detail =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
          ?? "Could not send the invite. Check the mail configuration.";
        confirm({
          title: "Couldn't send invite",
          description: detail,
          confirmLabel: "OK",
          hideCancel: true,
        });
      },
    });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil className="mr-2 h-4 w-4" />Edit
        </DropdownMenuItem>
        {row.is_active && (
          <DropdownMenuItem
            disabled={resend.isPending}
            onSelect={(e) => { e.preventDefault(); onResend(); }}
          >
            <Mail className="mr-2 h-4 w-4" />
            {resend.isPending ? "Sending…" : "Resend invite"}
          </DropdownMenuItem>
        )}
        {row.is_active && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              disabled={deactivate.isPending}
              onSelect={(e) => { e.preventDefault(); onDeactivate(); }}
            >
              <UserMinus className="mr-2 h-4 w-4" />
              {deactivate.isPending ? "Deactivating…" : "Deactivate"}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
